import {
	ReactElement,
	useCallback,
	useContext
} from "react";
import { useRenderManager } from "../useRenderManager";
import { OneByOneTileContent } from "./OneByOneTileContent";
import { TwoByTwoTileContent } from "./TwoByTwoTileContent";
import { navigateToAzureApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "./useRegisterTileUsage";
import { AppType } from "client/Api/Apps/common";
import { InteractionState, TileContext } from "./TileContext";
import { Anchor, Icon } from "client/Components";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { TileRenderState } from "../RenderState/common";
import {
	useBaseNavigation,
	useConditionalAccessNavigation,
	useMaintenanceWindowNavigation
} from "./useInvokeNavigation";
import { TimeZone } from "client/DateAndTime/timezones";
import { TileContextProvider } from "../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";
import styles from "./AzureAppTileContent.module.less";

export type AzureAppTileContentProps = {
	renderState: TileRenderState<AppType.AzureApp>;
	onEdit: (() => void) | undefined;
	userTimezone: TimeZone;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const AzureAppTileContent = (props: AzureAppTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		userTimezone,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = useAzureAppTileNavigation(
		renderState,
		onEdit,
		userTimezone
	);

	const { dimension } = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<>
				<TwoByTwoTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
					customStatusIcon={
						renderState.interactionState === InteractionState.Disabled
							? <ChangeAppCredentialsAnchor />
							: undefined
					}
				/>
			</>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<>
					<OneByOneTileContent
						onClick={navigateHandler}
						onOpenTileMenu={onOpenTileMenu}
						customStatusIcon={
							renderState.interactionState === InteractionState.Disabled
								? <ChangeAppCredentialsAnchor />
								: undefined
						}
					/>
				</>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
AzureAppTileContent.displayName = "AzureAppTileContent";

const ChangeAppCredentialsAnchor = (): ReactElement => {
	const { id } = useContext(TileContext);

	return (
		<Anchor
			className={styles.changeAppCredentials}
			target="_blank"
			href={createUrlRelativeToEnvironment(`Workspace/GoChangeCredentials/${id}`)}
		>
			<Icon value="key" />
		</Anchor>
	);
};
ChangeAppCredentialsAnchor.displayName = "ChangeAppCredentialsAnchor";

export function useAzureAppTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.AzureApp>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined,
	userTimezone: TimeZone
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	const {
		environmentAppId,
		settings: {
			destinationWindow
		},
		conditionalAccess,
		maintenanceWindow
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useConditionalAccessNavigation(
			conditionalAccess,
			useMaintenanceWindowNavigation(
				maintenanceWindow,
				userTimezone,
				useCallback(
					(): void => {
						registerTileUsage();

						navigateToAzureApp({
							id: environmentAppId,
							destinationWindow
						});
					},
					[
						environmentAppId,
						registerTileUsage,
						destinationWindow
					]
				)
			)
		)
	);
}
